import { useMetricBreakdown } from "modules/projects/hooks/useMetricBreakdowns"
import { useProjectsList } from "modules/projects/hooks/useProjectsList"
import useMetricsConfigurationBySlug from "modules/projects/OverviewTab/useMetricsConfigurationBySlug"
import { useMemo } from "react"
import { toSlug } from "../../utils"

/**
 * Returns a list of top projects by market sector slug sorted by a given metric id.
 * @example
 * const { data: topProjectsBySectorSlug } = useTopProjectsBySectorSlug('fees')
 */

export const useTopProjectsBySectorSlug = (metricId: string) => {
  const metricConfiguration = useMetricsConfigurationBySlug(toSlug(metricId))
  const { data: { data: breakdownData = [] } = {} } = useMetricBreakdown({
    metricIds: [metricId],
    interval: "365d",
    groupBy: "projects",
  })

  const valueByDataId = useMemo(() => {
    if (!metricConfiguration) return {}
    return breakdownData.reduce(
      (acc, curr) => {
        const isStatic = metricConfiguration.static
        acc[curr.data_id] = isStatic
          ? curr.metrics[metricId]?.latest
          : curr.metrics[metricId]?.sum
        return acc
      },
      {} as Record<string, number | undefined>
    )
  }, [breakdownData, metricConfiguration, metricId])

  const { data: projects = [] } = useProjectsList()

  return useMemo(() => {
    // Create a map of sectors to projects, sorted by metric value and limited to top 3
    const sectorProjectMap = {} as Record<
      string,
      {
        project: (typeof projects)[number]
        value: number | undefined
      }[]
    >

    // First, group projects by sector
    projects?.forEach((project) => {
      project.market_sectors.forEach((sectorId) => {
        const slug = toSlug(sectorId)
        if (!sectorProjectMap[slug]) {
          sectorProjectMap[slug] = []
        }
        sectorProjectMap[slug].push({
          project,
          value: valueByDataId[project.data_id],
        })
      })
    })

    // Then, sort each sector's projects by the metric value and limit to top 3
    Object.keys(sectorProjectMap).forEach((slug) => {
      sectorProjectMap[slug] = sectorProjectMap[slug]
        .sort((a, b) => {
          const aValue = a.value || 0
          const bValue = b.value || 0
          return bValue - aValue // Sort descending
        })
        .slice(0, 3) // Limit to top 3 projects
    })

    return sectorProjectMap
  }, [projects, valueByDataId])
}
